import { createSelector } from 'reselect';
import { convertBaggageObject, convertMealObject } from '../../types';
import { Measurement } from '../../../BaggageService/types';
import { getDefaultTimelineStep, getGdsServiceMinPrice, getIsBrandIncludedService, getServicePrice, isPetBaggage } from '../../utils';
import { parseIncludedBaggage } from '../../../BaggagePage/utils';
import { getFlatArraySet, getHasRequestedServices, getSeatMapUniqueAvailableSegments, getSelectedInsurancesByTypes, getTotalPrice, isOdd } from '../../../utils';
import { getImportantInsuranceCodesByAvailableCodes } from '../../../InsurancePage/utils';
import { getHasSelectedSeats, getPassengerStateCompatible, getSelectedServicesState } from '../selectedServices/selectors';
import { useConfig } from '../../../context';
import { AviaPassengerType, BaggageType, ExareStatus, FlightSegmentStatus, GdsServiceVipSubType, InsuranceType, OrderAdditionalServiceGdsServiceServiceType, OrderStatus, PaymentStatusEnum, TimelineStep, TravellerFieldEnum, NASStatus } from '@websky/graphql';
import { getRelatedSegments } from '../../../Baggage/UpgradeBaggageDialog/utils';
import { Currency } from '../../../enums';
import { BUSINESS_RULES_CHECKBOXES_CODES } from '@shared/config/constants/app';
import { getTravellerContactInfoValues } from '@shared/lib/passengers';
import { getMiscServiceGroups as getMiscServiceGroupsFn } from '@shared/lib/gdsService';
import { getAggregatorGroupType } from '@entities/order/service';
export const getSelectedTravellerServices = (gdsServices, travellers, confirmationStatus = 'notConfirmed') => {
    const selectedServices = [];
    gdsServices.forEach(service => {
        const allowedPassengerIdSet = getFlatArraySet(service.allowedPassengers);
        if (allowedPassengerIdSet.size) {
            travellers.forEach(traveller => {
                var _a, _b, _c;
                if (allowedPassengerIdSet.has(traveller.id)) {
                    const allowedSegmentIdSet = getFlatArraySet(service.allowedSegments);
                    if (allowedSegmentIdSet.size) {
                        (_c = (_b = (_a = traveller.services) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) === null || _c === void 0 ? void 0 : _c.forEach(selectedService => {
                            var _a;
                            if (selectedService.serviceId === service.id &&
                                selectedService.segmentIds.every(id => allowedSegmentIdSet.has(id)) &&
                                (confirmationStatus === 'confirmed'
                                    ? selectedService.confirmedCount > 0
                                    : confirmationStatus === 'notConfirmed'
                                        ? selectedService.confirmedCount === 0
                                        : true)) {
                                selectedServices.push(Object.assign(Object.assign({}, service), { price: Object.assign(Object.assign({}, service.price), { amount: service.price.amount * (((_a = selectedService.segmentIds) === null || _a === void 0 ? void 0 : _a.length) || 1) }) }));
                            }
                        });
                    }
                }
            });
        }
    });
    return selectedServices;
};
export const getSelectedServicesTotalPrice = (services) => {
    if (!services.length) {
        return null;
    }
    const price = getTotalPrice(services, service => service.price);
    if (price.amount === 0) {
        return null;
    }
    return price;
};
const getState = (state) => state;
export const getOrder = createSelector(getState, state => state.order);
export const getShowServicesBeforeBooking = createSelector(getOrder, order => { var _a; return (_a = order.showServicesBeforeBooking) !== null && _a !== void 0 ? _a : true; });
export const getPaymentStatus = createSelector(getOrder, order => order.paymentStatus);
export const getIsBlockedForPayment = createSelector(getOrder, order => order.isBlockedForPayment);
export const getFlight = createSelector(getOrder, order => order.flight);
export const getSegmentsValues = createSelector(getFlight, flight => flight.segmentGroups.map(segment => {
    const firstDeparture = segment.segments[0].departure;
    const lastArrival = segment.segments[segment.segments.length - 1].arrival;
    const departureCityName = firstDeparture.airport.city.name;
    const arrivalCityName = lastArrival.airport.city.name;
    return {
        id: segment.groupId,
        label: [departureCityName, arrivalCityName].join(' — '),
        departure: {
            iata: firstDeparture.airport.iata,
            cityName: departureCityName
        },
        arrival: {
            iata: lastArrival.airport.iata,
            cityName: arrivalCityName
        },
        date: new Date(firstDeparture.date)
    };
}));
export const getCanceledFlights = createSelector(getFlight, flight => flight.segmentGroups.filter(group => {
    return group.segments.some(segment => segment.status === FlightSegmentStatus.Canceled);
}));
export const getAdditionalServices = createSelector(getOrder, order => order.additionalServices);
export const getServices = createSelector(getOrder, order => { var _a, _b; return (_b = (_a = order.additionalServices.gdsServices) === null || _a === void 0 ? void 0 : _a.services) !== null && _b !== void 0 ? _b : []; });
export const getGdsServicesById = createSelector(getServices, services => {
    const servicesMap = services.reduce((acc, service) => {
        acc[service.id] = service;
        return acc;
    }, {});
    return servicesMap;
});
export const getEveryPortServices = createSelector(getOrder, order => order.additionalServices.nas);
export const getEveryPortServicesToRefund = createSelector(getEveryPortServices, services => {
    var _a, _b, _c;
    const selectedServices = [];
    const nasServicesMap = new Map((_b = (_a = services === null || services === void 0 ? void 0 : services.services) === null || _a === void 0 ? void 0 : _a.map(service => [service.id, service])) !== null && _b !== void 0 ? _b : []);
    (_c = services === null || services === void 0 ? void 0 : services.selectedServices) === null || _c === void 0 ? void 0 : _c.forEach(selectedService => {
        const nasService = nasServicesMap.get(selectedService.product.id);
        if (nasService &&
            selectedService.orders.some(order => order.isRefundable && order.status === NASStatus.Confirmed)) {
            selectedServices.push(Object.assign(Object.assign({}, selectedService), { product: nasService }));
        }
    });
    return selectedServices;
});
export const getIsShowRefundNasServices = createSelector(getEveryPortServicesToRefund, services => !!services.length);
export const getRecheckAvailabilityTimout = createSelector(getOrder, order => order.timeoutToReCheckAvailability);
export const getActualizationResult = createSelector(getOrder, order => order.actualizationResult);
export const getIsNeedToHideFlightPrice = createSelector(getOrder, order => (order.hideItFares || false) && [OrderStatus.Confirmed, OrderStatus.Cancelled].includes(order.status));
export const getTravellers = createSelector(getOrder, order => order.travellers);
export const getIsFfpSupported = createSelector(getOrder, order => { var _a; return (_a = order.ffpInfo) === null || _a === void 0 ? void 0 : _a.ffpSupported; });
export const getTravellersValues = createSelector(getTravellers, travellers => travellers.map(traveller => ({
    id: traveller.id,
    type: traveller.type,
    supplierTravellerType: traveller.supplierTravellerType,
    linkedTraveller: traveller.linkedTraveller
        ? {
            id: traveller.linkedTraveller.id,
            type: traveller.linkedTraveller.type,
            values: getTravellerContactInfoValues(traveller.linkedTraveller.values)
        }
        : null,
    values: getTravellerContactInfoValues(traveller.values)
})));
export const getExareInfo = createSelector(getOrder, order => order.exareInfo);
export const getLastExchange = createSelector(getExareInfo, exareInfo => exareInfo === null || exareInfo === void 0 ? void 0 : exareInfo.lastExchange);
export const getLastRefund = createSelector(getExareInfo, exareInfo => exareInfo === null || exareInfo === void 0 ? void 0 : exareInfo.lastRefund);
export const getOrderLastExchangeStatus = createSelector(getLastExchange, lastExchange => lastExchange === null || lastExchange === void 0 ? void 0 : lastExchange.status);
export const getIsOpenedLastExchange = createSelector(getOrderLastExchangeStatus, status => status &&
    [
        ExareStatus.InProcess,
        ExareStatus.AwaitingPayment,
        ExareStatus.NeedConfirm,
        ExareStatus.TimelimitExpired
    ].includes(status));
export const getIsAwaitingPaymentExchange = createSelector(getOrderLastExchangeStatus, status => status && status === ExareStatus.AwaitingPayment);
export const getIsExchangeAutoMode = createSelector(getExareInfo, exareInfo => !!(exareInfo === null || exareInfo === void 0 ? void 0 : exareInfo.exchangeAutoMode));
export const getLastExchangeFlights = createSelector(getLastExchange, lastExchange => {
    var _a;
    if (!lastExchange) {
        return null;
    }
    return (((_a = lastExchange.flight) === null || _a === void 0 ? void 0 : _a.segments) || [])
        .filter(segment => segment.segment.status !== FlightSegmentStatus.Confirmed)
        .map(segment => (Object.assign(Object.assign({}, lastExchange.flight), { segments: [segment] })));
});
export const getBonusForFlight = createSelector(getOrder, order => { var _a; return (_a = order.ffpInfo) === null || _a === void 0 ? void 0 : _a.bonusForFlight; });
export const getPriceInMiles = createSelector(getOrder, order => { var _a; return (_a = order.price) === null || _a === void 0 ? void 0 : _a.totalInMiles; });
export const getCustomerSubscribed = createSelector(getOrder, order => order.customer.subscribed);
export const getAvailableServices = createSelector(getServices, services => {
    const availableGdsServices = {};
    services.forEach(service => {
        const serviceType = service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage
            ? service.baggageType
            : service.type;
        if (!availableGdsServices.hasOwnProperty(serviceType)) {
            availableGdsServices[serviceType] = serviceType;
        }
    });
    return availableGdsServices;
});
export const getSegmentGroups = createSelector(getOrder, order => { var _a; return (_a = order.flight) === null || _a === void 0 ? void 0 : _a.segmentGroups; });
export const getFareFamiliesMap = createSelector(getSegmentGroups, segmentGroups => {
    const fareFamiliesMap = new Map();
    segmentGroups.forEach(segmentGroup => {
        const fareFamily = segmentGroup.fareFamily;
        if (!fareFamily) {
            return;
        }
        if (!fareFamiliesMap.has(fareFamily.id)) {
            fareFamiliesMap.set(fareFamily.id, {
                fareFamily,
                segmentIds: []
            });
        }
        segmentGroup.segments.forEach(segment => fareFamiliesMap.get(fareFamily.id).segmentIds.push(segment.id));
    });
    return fareFamiliesMap;
});
export const getIsOrderBlockedForPayment = createSelector(getOrder, order => order.isBlockedForPayment);
export const getOldOrderPrice = createSelector(getOrder, order => { var _a; return (_a = order === null || order === void 0 ? void 0 : order.actualizationResult) === null || _a === void 0 ? void 0 : _a.oldPrice; });
export const getNewOrderPrice = createSelector(getOrder, order => { var _a; return (_a = order === null || order === void 0 ? void 0 : order.actualizationResult) === null || _a === void 0 ? void 0 : _a.newPrice; });
export const getFareLockService = createSelector(getServices, services => services.find(service => service.isPriceFreeze));
export const getIsFareLockSelected = createSelector(getTravellers, getFareLockService, (travellers, fareLockService) => travellers === null || travellers === void 0 ? void 0 : travellers.some(traveller => { var _a, _b, _c; return (_c = (_b = (_a = traveller === null || traveller === void 0 ? void 0 : traveller.services) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) === null || _c === void 0 ? void 0 : _c.some(service => (service === null || service === void 0 ? void 0 : service.serviceId) === (fareLockService === null || fareLockService === void 0 ? void 0 : fareLockService.id)); }));
export const getIsFareLockConfirmed = createSelector(getTravellers, getFareLockService, (travellers, fareLockService) => travellers === null || travellers === void 0 ? void 0 : travellers.some(traveller => {
    var _a, _b, _c;
    return (_c = (_b = (_a = traveller === null || traveller === void 0 ? void 0 : traveller.services) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) === null || _c === void 0 ? void 0 : _c.some(service => (service === null || service === void 0 ? void 0 : service.serviceId) === (fareLockService === null || fareLockService === void 0 ? void 0 : fareLockService.id) && (service === null || service === void 0 ? void 0 : service.confirmedCount));
}));
export const getIsPromoCodeAppliedToFlight = createSelector(getOldOrderPrice, getNewOrderPrice, (oldPrice, newPrice) => (newPrice === null || newPrice === void 0 ? void 0 : newPrice.amount) < (oldPrice === null || oldPrice === void 0 ? void 0 : oldPrice.amount));
const _getIncludedBaggage = createSelector(getFareFamiliesMap, getTravellers, useConfig, (fareFamiliesMap, travellers, config) => {
    var _a, _b;
    if (['N4', 'DV', 'YC'].includes((_b = (_a = config === null || config === void 0 ? void 0 : config.global) === null || _a === void 0 ? void 0 : _a.companyInfo) === null || _b === void 0 ? void 0 : _b.iataCode)) {
        // filter out all included baggage for INF and INS
        const travellersWithoutInfants = travellers.filter(traveller => traveller.type !== AviaPassengerType.INF && traveller.type !== AviaPassengerType.INS);
        return fareFamiliesMap
            ? parseIncludedBaggage(fareFamiliesMap, travellersWithoutInfants.map(({ id }) => id))
            : [];
    }
    return fareFamiliesMap
        ? parseIncludedBaggage(fareFamiliesMap, travellers.map(({ id }) => id))
        : [];
});
export const getBrandIncludedBaggage = createSelector(getTravellers, travellers => {
    const includedBaggages = [];
    travellers.forEach(traveller => {
        var _a, _b;
        return (_b = (_a = traveller.services) === null || _a === void 0 ? void 0 : _a.brandIncludedServices) === null || _b === void 0 ? void 0 : _b.services.filter(services => services.service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage).forEach(service => {
            var _a;
            const baggageService = service.service;
            const baggage = {
                id: service.serviceId,
                segmentIds: baggageService.allowedSegments,
                includedSegments: service.segmentIds,
                type: baggageService.baggageType,
                name: baggageService.name,
                description: baggageService.description,
                value: {
                    amount: parseInt((_a = baggageService.baggageWeight) === null || _a === void 0 ? void 0 : _a.split('x')[0]),
                    measurement: Measurement.Kilograms
                },
                size: baggageService.size,
                included: service.confirmedCount,
                price: baggageService.price,
                rfics: baggageService.rfisc,
                canBeAdded: false,
                allowedPassengers: [traveller.id],
                isReplacedBy: baggageService.isReplacedBy
            };
            includedBaggages.push(Object.assign(Object.assign({}, service), { passengerId: traveller.id, baggage }));
        });
    });
    return includedBaggages;
});
export const getMiscServices = createSelector(getServices, services => services.filter(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Misc));
export const getMiscServiceGroups = createSelector(getServices, getMiscServiceGroupsFn);
export const getAnimalServices = createSelector(getServices, services => services.filter(service => [BaggageType.PetInHandCarrier].includes(service.baggageType)));
export const getSelectedTravellerAnimalServices = createSelector(getAnimalServices, getTravellers, getSelectedTravellerServices);
export const getSelectedAnimalServicesPrice = createSelector(getSelectedTravellerAnimalServices, getSelectedServicesTotalPrice);
export const hasSeatsServices = createSelector(getServices, services => services.some(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Seat));
export const hasBaggageServices = createSelector(getServices, services => services.some(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage));
export const getSeatMap = createSelector(getOrder, order => order.flight.seatMap);
export const getOrderId = createSelector(getOrder, order => order.id);
export const getOrderHash = createSelector(getOrder, order => order.stateHash);
export const getIsOddOrderId = createSelector(getOrderId, isOdd);
export const getIsNew = createSelector(getOrder, order => order.status === OrderStatus.New);
export const getIsNotNew = createSelector(getOrder, order => order.status !== OrderStatus.New);
export const getIsTravellersDataFilled = createSelector(getOrder, order => order.isTravellersDataFilled);
export const getIsReadonly = createSelector(getIsTravellersDataFilled, getIsNew, (isTravellersDataFilled, isNew) => isTravellersDataFilled && !isNew);
export const getIsRedirectFromMetasearch = createSelector(getOrder, order => order.redirectFromMetasearch);
export const getOrderStatus = createSelector(getOrder, order => order.status);
export const getHasAvailableSeats = createSelector(getSeatMap, seatMap => { var _a; return (_a = seatMap === null || seatMap === void 0 ? void 0 : seatMap.segments) === null || _a === void 0 ? void 0 : _a.some(({ isAnySeatExistsAndFree }) => isAnySeatExistsAndFree); });
export const getIsAllSegmentsAvailableOnlyOnCheckin = createSelector(getSeatMap, seatMap => seatMap.segments.every(segment => segment.isSeatsAvailableInCheckinOnly === true));
export const getIsBookedOrConfirmed = createSelector(getOrder, order => order.status === OrderStatus.Booked || order.status === OrderStatus.Confirmed);
export const getTotalInsurancePrice = createSelector(getOrder, state => state.price.insurance);
export const getIsBooked = createSelector(getOrder, order => order.status === OrderStatus.Booked);
export const getIsConfirmed = createSelector(getOrder, order => order.status === OrderStatus.Confirmed);
export const getIsConfirmedOrCancelled = createSelector(getOrder, order => order.status === OrderStatus.Confirmed || order.status === OrderStatus.Cancelled);
export const getIsPriceFreezeProductSelected = createSelector(getOrder, order => order.isPriceFreezeProductSelected);
export const getIsCancelled = createSelector(getOrder, order => order.status === OrderStatus.Cancelled);
export const getIsFFPUsed = createSelector(getOrder, order => !!order.ffpInfo);
export const getMilesForOrder = createSelector(getOrder, order => { var _a; return (_a = order.ffpInfo) === null || _a === void 0 ? void 0 : _a.bonusForFlight; });
// comment for next php release
export const getFfpBonusesForFlightByTravellers = createSelector(getOrder, order => {
    var _a, _b;
    const orderTravellersById = {};
    for (const traveller of order.travellers) {
        orderTravellersById[traveller.id] = null;
    }
    (_b = (_a = order.ffpInfo) === null || _a === void 0 ? void 0 : _a.bonusByCard) === null || _b === void 0 ? void 0 : _b.forEach(travellerBonus => {
        const orderTraveller = order.travellers.find(traveller => {
            return traveller.values.some(value => value.type === TravellerFieldEnum.LoyaltyNumber && value.value === travellerBonus.cardNumber);
        });
        if (orderTraveller) {
            orderTravellersById[orderTraveller.id] = Object.assign(Object.assign({}, travellerBonus), { cardNumber: travellerBonus.cardNumber });
        }
    });
    return orderTravellersById;
});
export const getAvailableGdsServicesByType = createSelector(getOrder, getSeatMap, getHasSelectedSeats, (order, seatMap, hasSelectedSeats) => {
    var _a, _b, _c, _d, _e;
    const availableServices = {};
    (_c = (_b = (_a = order.additionalServices) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) === null || _c === void 0 ? void 0 : _c.forEach(service => {
        // TODO: check that service.canBeAdded = true when php fix it
        if (!availableServices.hasOwnProperty(service.type)) {
            availableServices[service.type] = true;
        }
    });
    const hasPassengersAvailableForSeatSelection = (_d = order.travellers) === null || _d === void 0 ? void 0 : _d.some(traveller => traveller.type === AviaPassengerType.ADT ||
        traveller.type === AviaPassengerType.CLD ||
        traveller.type === AviaPassengerType.INS);
    return Object.assign(Object.assign(Object.assign(Object.assign({}, availableServices), {
        [OrderAdditionalServiceGdsServiceServiceType.Seat]: (hasPassengersAvailableForSeatSelection &&
            ((_e = seatMap === null || seatMap === void 0 ? void 0 : seatMap.segments) === null || _e === void 0 ? void 0 : _e.some(segment => segment.availableSeatsCount > 0 || segment.isSeatsAvailableInCheckinOnly))) ||
            hasSelectedSeats
    }), {
        [OrderAdditionalServiceGdsServiceServiceType.Baggage]: availableServices.Baggage ||
            getIsBrandIncludedService(order, OrderAdditionalServiceGdsServiceServiceType.Baggage)
    }), {
        [OrderAdditionalServiceGdsServiceServiceType.Meal]: availableServices.Meal ||
            getIsBrandIncludedService(order, OrderAdditionalServiceGdsServiceServiceType.Meal)
    });
});
export const getPaymentIsRequired = createSelector(getOrder, order => order.paymentStatus !== PaymentStatusEnum.Full);
export const getCustomer = createSelector(getOrder, order => order === null || order === void 0 ? void 0 : order.customer);
export const getIsContactsInfoIsEmpty = createSelector(getCustomer, customer => customer.contactInfoIsEmpty);
export const isRoundTrip = createSelector(getOrder, (order) => {
    var _a;
    const segmentGroups = (_a = order.flight) === null || _a === void 0 ? void 0 : _a.segmentGroups;
    if (segmentGroups.length !== 2) {
        return false;
    }
    const outbound = segmentGroups[0];
    const outboundDeparture = outbound.segments[0].departure.airport.iata;
    const outboundArrival = outbound.segments[outbound.segments.length - 1].arrival.airport.iata;
    const inbound = segmentGroups[1];
    const inboundDeparture = inbound.segments[0].departure.airport.iata;
    const inboundArrival = inbound.segments[inbound.segments.length - 1].arrival.airport.iata;
    return outboundArrival === inboundDeparture && outboundDeparture === inboundArrival;
});
export const getSegmentsForServices = createSelector(getOrder, (order) => {
    const segments = [];
    order.flight.segmentGroups.forEach(group => group.segments.forEach(segment => {
        var _a;
        segments.push({
            id: segment.id,
            arrival: segment.arrival,
            departure: segment.departure,
            amenities: (_a = segment.flightInfo) === null || _a === void 0 ? void 0 : _a.amenities,
            groupId: group.groupId
        });
    }));
    return segments;
});
export const getSeatMapAvailableSegments = createSelector(getSeatMap, seatMap => getSeatMapUniqueAvailableSegments(seatMap.segments));
export const getSegmentsForBaggage = createSelector(getOrder, (order) => {
    return order.flight.segments.map(seg => ({
        id: seg.segment.id,
        departure: seg.segment.departure,
        arrival: seg.segment.arrival
    }));
});
export const getSegments = createSelector(getOrder, (order) => order.flight.segments.map(segment => segment.segment));
export const getSegmentIds = createSelector(getOrder, order => order.flight.segments.map(segment => segment.segment.id));
export const getMessages = createSelector(getOrder, order => order.messages);
export const getBusinessRulesCheckboxMessages = createSelector(getMessages, messages => messages.filter(({ code }) => BUSINESS_RULES_CHECKBOXES_CODES.includes(code)));
export const getBaggageMinPrice = createSelector(getOrder, order => getGdsServiceMinPrice(order, OrderAdditionalServiceGdsServiceServiceType.Baggage));
export const getGroundTransportationServices = createSelector(getServices, services => {
    return services.filter(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Vip &&
        service.subType ===
            GdsServiceVipSubType.GroundTransportation);
});
export const getSelectedTravellerGroundTransportationServices = createSelector(getGroundTransportationServices, getTravellers, getSelectedTravellerServices);
export const getSelectedGroundTransportationServicesPrice = createSelector(getSelectedTravellerGroundTransportationServices, getSelectedServicesTotalPrice);
export const getUpgradeBaggageMinPrice = createSelector(getOrder, order => getGdsServiceMinPrice(order, OrderAdditionalServiceGdsServiceServiceType.Baggage, [BaggageType.HandLuggageExcess]));
export const isShowAnimalInSeparateCard = createSelector(useConfig, config => config.global.servicesAvailable.animal);
export const getSpecialEquipmentBaggageTypes = createSelector(isShowAnimalInSeparateCard, showAnimalInSeparateCard => [
    !showAnimalInSeparateCard && BaggageType.PetInHandCarrier,
    BaggageType.PetInCabin,
    BaggageType.PetInHold,
    BaggageType.SportingEquipment,
    BaggageType.MusicalInstrument,
    BaggageType.FishingEquipment,
    BaggageType.SurfingEquipment,
    BaggageType.Ski,
    BaggageType.Bike
].filter(Boolean));
export const getSpecialEquipmentMinPrice = createSelector(getOrder, getSpecialEquipmentBaggageTypes, (order, specialEquipmentBaggageTypes) => getGdsServiceMinPrice(order, OrderAdditionalServiceGdsServiceServiceType.Baggage, specialEquipmentBaggageTypes));
export const getOverweightMinPrice = createSelector(getOrder, order => getGdsServiceMinPrice(order, OrderAdditionalServiceGdsServiceServiceType.Baggage, [BaggageType.BaggageExcess]));
export const getCurrencyFromOrder = createSelector(getOrder, order => { var _a, _b; return (_b = (_a = order.price) === null || _a === void 0 ? void 0 : _a.total) === null || _b === void 0 ? void 0 : _b.currency; });
export const getSeatsMinPrice = createSelector(getOrder, order => {
    var _a, _b, _c, _d, _e;
    let minPrice = 0;
    const currency = (_c = (_b = (_a = order.additionalServices.gdsServices) === null || _a === void 0 ? void 0 : _a.services) === null || _b === void 0 ? void 0 : _b.find(service => { var _a; return !!((_a = service === null || service === void 0 ? void 0 : service.price) === null || _a === void 0 ? void 0 : _a.currency); })) === null || _c === void 0 ? void 0 : _c.price.currency;
    (_e = (_d = order.flight.seatMap) === null || _d === void 0 ? void 0 : _d.segments) === null || _e === void 0 ? void 0 : _e.forEach(segment => {
        var _a;
        (_a = segment === null || segment === void 0 ? void 0 : segment.decks) === null || _a === void 0 ? void 0 : _a.forEach(deck => {
            var _a;
            (_a = deck === null || deck === void 0 ? void 0 : deck.rows) === null || _a === void 0 ? void 0 : _a.forEach(row => {
                row === null || row === void 0 ? void 0 : row.seats.forEach(seat => {
                    var _a, _b;
                    if (!(seat === null || seat === void 0 ? void 0 : seat.isAvailable)) {
                        return;
                    }
                    if (!minPrice) {
                        minPrice = (_a = seat === null || seat === void 0 ? void 0 : seat.price) === null || _a === void 0 ? void 0 : _a.amount;
                    }
                    if (((_b = seat === null || seat === void 0 ? void 0 : seat.price) === null || _b === void 0 ? void 0 : _b.amount) < minPrice) {
                        minPrice = seat.price.amount;
                    }
                });
            });
        });
    });
    return {
        currency,
        amount: minPrice
    };
});
export const getMealMinPrice = createSelector(getOrder, order => getGdsServiceMinPrice(order, OrderAdditionalServiceGdsServiceServiceType.Meal));
// #todo: add tests
export const getSearchParameters = createSelector(getOrder, getCurrencyFromOrder, ({ flight, travellers }, orderCurrency) => {
    const passengersMap = {};
    const passengers = [];
    travellers.forEach(({ type }) => {
        if (passengersMap.hasOwnProperty(type)) {
            passengersMap[type]++;
        }
        else {
            passengersMap[type] = 1;
        }
    });
    for (const passengerType in passengersMap) {
        if (passengersMap.hasOwnProperty(passengerType)) {
            passengers.push({
                passengerType: passengerType,
                extendedPassengerType: null,
                count: passengersMap[passengerType]
            });
        }
    }
    return {
        promotionCode: null,
        currency: orderCurrency,
        passengers,
        segments: flight.segmentGroups.map(group => {
            const firstSegment = group.segments[0];
            const lastSegment = group.segments[group.segments.length - 1];
            return {
                date: firstSegment.departure.date,
                departure: {
                    name: firstSegment.departure.airport.name,
                    iata: firstSegment.departure.airport.iata,
                    city: {
                        name: firstSegment.departure.airport.city.name
                    }
                },
                arrival: {
                    name: lastSegment.arrival.airport.name,
                    iata: lastSegment.arrival.airport.iata,
                    city: {
                        name: lastSegment.arrival.airport.city.name
                    }
                }
            };
        }),
        ffpMode: false
    };
});
// check that canAddServices is boolean ( because in CheckinOrder there is no such props and shouldRedirectToFallback always returns true )
export const shouldRedirectToFallback = createSelector(getOrder, ({ canAddServices, canRemoveServices }) => typeof canAddServices === 'boolean' &&
    typeof canRemoveServices === 'boolean' &&
    (!canAddServices || !canRemoveServices));
export const getServicesFallbackURL = createSelector(getOrder, ({ servicesFallbackURL }) => servicesFallbackURL);
export const getAeroexpress = createSelector(getOrder, order => { var _a, _b; return (_b = (_a = order.additionalServices) === null || _a === void 0 ? void 0 : _a.transfer) === null || _b === void 0 ? void 0 : _b.aeroexpress; });
export const getInsurances = createSelector(getOrder, order => { var _a; return (_a = order.additionalServices) === null || _a === void 0 ? void 0 : _a.insurances; });
export const getInsurancePrograms = createSelector(getInsurances, insurances => { var _a; return (_a = insurances === null || insurances === void 0 ? void 0 : insurances.insurancePrograms) !== null && _a !== void 0 ? _a : []; });
export const getCanAddedInsurancePrograms = createSelector(getInsurancePrograms, insurancePrograms => insurancePrograms.filter(program => program.canBeAdded));
export const getInsurancesGroups = createSelector(getInsurances, insurances => insurances === null || insurances === void 0 ? void 0 : insurances.insuranceProgramGroups);
export const getSelectedInsurances = createSelector(getInsurances, insurances => { var _a; return (_a = insurances === null || insurances === void 0 ? void 0 : insurances.selectedInsurances) !== null && _a !== void 0 ? _a : []; });
export const getSelectedVskInsurancesByType = createSelector(getSelectedInsurances, selectedInsurances => getSelectedInsurancesByTypes(selectedInsurances, [InsuranceType.Vsk]));
const getGroupInsurances = (programs = [], groups = []) => {
    if (!programs.length) {
        return [];
    }
    if (!groups.length) {
        return [{ groupName: null, insurancePrograms: programs }];
    }
    const programMap = new Map(programs.map(program => [program.code, program]));
    return groups
        .map(group => {
        const programsByGroup = group.insurancePrograms.map(({ code }) => programMap.get(code)).filter(Boolean);
        return {
            groupName: group.groupValue,
            insurancePrograms: programsByGroup
        };
    })
        .filter(group => group.insurancePrograms.length);
};
export const getInsurancesByGroups = createSelector(getInsurancePrograms, getInsurancesGroups, getGroupInsurances);
export const getCanBeAddedInsurancesByGroups = createSelector(getCanAddedInsurancePrograms, getInsurancesGroups, getGroupInsurances);
export const getAvailableInsuranceCodes = createSelector(getInsurances, insurance => {
    var _a;
    const availableInsuranceCodeSet = new Set();
    if ((_a = insurance === null || insurance === void 0 ? void 0 : insurance.insurancePrograms) === null || _a === void 0 ? void 0 : _a.length) {
        insurance.insurancePrograms.forEach(insurance => availableInsuranceCodeSet.add(insurance.code));
    }
    return [...availableInsuranceCodeSet];
});
export const getSelectedInsuranceCodes = createSelector(getInsurances, insurance => {
    var _a;
    const selectedInsuranceCodeSet = new Set();
    if ((_a = insurance === null || insurance === void 0 ? void 0 : insurance.selectedInsurances) === null || _a === void 0 ? void 0 : _a.length) {
        insurance.selectedInsurances.forEach(insurance => selectedInsuranceCodeSet.add(insurance.insuranceProgram.code));
    }
    return [...selectedInsuranceCodeSet];
});
export const getImportantInsuranceCodes = (availableInsuranceCodes = []) => createSelector(getInsurances, insurances => getImportantInsuranceCodesByAvailableCodes(insurances, availableInsuranceCodes));
export const getAvailableInsuranceCodesByPrograms = (insurancePrograms) => createSelector(getAvailableInsuranceCodes, availableCodes => {
    if (!(insurancePrograms === null || insurancePrograms === void 0 ? void 0 : insurancePrograms.length)) {
        return availableCodes;
    }
    return availableCodes.filter(code => insurancePrograms.some(program => program.code === code));
});
export const getAvailableSelectedInsuranceCodes = (availableCodes, include = true) => createSelector(getSelectedInsuranceCodes, selectedCodes => {
    if (!(availableCodes === null || availableCodes === void 0 ? void 0 : availableCodes.length)) {
        return selectedCodes;
    }
    return selectedCodes.filter(code => {
        const isIncludeInsurance = availableCodes.includes(code);
        return include ? isIncludeInsurance : !isIncludeInsurance;
    });
});
export const getInsuranceCodeBySalePriority = createSelector(getInsurances, insurance => {
    var _a;
    const insuranceCodeBySalePriorityMap = new Map();
    if ((_a = insurance === null || insurance === void 0 ? void 0 : insurance.insurancePrograms) === null || _a === void 0 ? void 0 : _a.length) {
        insurance.insurancePrograms.forEach(insuranceProgram => {
            var _a, _b;
            const salePriority = (_b = (_a = insuranceProgram.extraParams) === null || _a === void 0 ? void 0 : _a.find(param => param.key === 'salePriority')) === null || _b === void 0 ? void 0 : _b.value;
            if (salePriority) {
                insuranceCodeBySalePriorityMap.set(insuranceProgram.code, +salePriority);
            }
        });
    }
    return insuranceCodeBySalePriorityMap;
});
export const getSortedPriorityInsurancePrograms = createSelector(getInsurances, getInsuranceCodeBySalePriority, (insurance, insuranceCodeBySalePriority) => {
    var _a;
    if (!((_a = insurance === null || insurance === void 0 ? void 0 : insurance.insurancePrograms) === null || _a === void 0 ? void 0 : _a.length) || !insuranceCodeBySalePriority.size) {
        return [];
    }
    return [...insurance.insurancePrograms]
        .filter(insuranceProgram => insuranceCodeBySalePriority.has(insuranceProgram.code))
        .sort((a, b) => {
        var _a, _b;
        const aPriority = (_a = insuranceCodeBySalePriority.get(a.code)) !== null && _a !== void 0 ? _a : 0;
        const bPriority = (_b = insuranceCodeBySalePriority.get(b.code)) !== null && _b !== void 0 ? _b : 0;
        return bPriority - aPriority;
    });
});
export const getPriorityInsuranceProgram = createSelector(useConfig, getSelectedInsuranceCodes, getSortedPriorityInsurancePrograms, (config, selectedInsuranceCodes, sortedPriorityInsurances) => {
    if (config.global.insurance.multiplePriorityInsurances) {
        return sortedPriorityInsurances.filter(insurance => !selectedInsuranceCodes.includes(insurance.code))[0];
    }
    return sortedPriorityInsurances[0];
});
function singleInsuranceProgramFilter(insuranceProgram, inversion) {
    return insuranceProgram.important !== inversion;
}
export const getSingleInsurancePrograms = (inversion = false) => createSelector(getInsurances, services => { var _a, _b; return (_b = (_a = services === null || services === void 0 ? void 0 : services.insurancePrograms) === null || _a === void 0 ? void 0 : _a.filter(i => singleInsuranceProgramFilter(i, inversion))) !== null && _b !== void 0 ? _b : []; });
export const getSelectedSingleInsurances = (inversion = false) => createSelector(getInsurances, services => services.selectedInsurances.filter(i => singleInsuranceProgramFilter(i.insuranceProgram, inversion)));
export const getMealServices = createSelector(getServices, (services) => services.filter(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Meal));
export const getBaggageServices = createSelector(getServices, (services) => services.filter(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage));
export const getVipServices = createSelector(getServices, (services) => services.filter(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Vip));
export const getMeals = createSelector(getMealServices, (meals) => meals.map(convertMealObject));
export const getBaggage = createSelector(getBaggageServices, (baggage) => baggage.map(convertBaggageObject));
export const getUpgradeClassServices = createSelector(getVipServices, vipServices => { var _a; return (_a = vipServices.filter(service => service.subType === GdsServiceVipSubType.Upgrade)) !== null && _a !== void 0 ? _a : []; });
const isUpgradeClassServiceSelected = (upgradeServices, travellers, segmentId) => {
    const upgradeServicesIds = upgradeServices.map(({ id }) => id);
    return travellers.some(traveller => {
        var _a, _b, _c;
        return (_c = (_b = (_a = traveller === null || traveller === void 0 ? void 0 : traveller.services) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) === null || _c === void 0 ? void 0 : _c.some(service => {
            return (upgradeServicesIds.includes(service.serviceId) &&
                (!segmentId || service.segmentIds.includes(segmentId)) &&
                (service.count > 0 || service.confirmedCount > 0));
        });
    });
};
export const getIsUpgradeClassServiceSelected = createSelector(getUpgradeClassServices, getTravellers, isUpgradeClassServiceSelected);
export const getIsUpgradeClassServiceSelectedBySegment = createSelector(getUpgradeClassServices, getTravellers, (_, segmentId) => segmentId, isUpgradeClassServiceSelected);
export const getIsUpgradeClassSelectedForAllSegments = createSelector(getUpgradeClassServices, getTravellers, getSegments, (upgradeServices, travellers, segments) => {
    var _a, _b, _c, _d;
    const upgradeServicesIds = upgradeServices.map(({ id }) => id);
    const upgradeSelectedOnSegments = {};
    for (const traveller of travellers) {
        const upgradeServices = (_d = (_c = (_b = (_a = traveller === null || traveller === void 0 ? void 0 : traveller.services) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) === null || _c === void 0 ? void 0 : _c.filter(service => upgradeServicesIds.includes(service.serviceId))) !== null && _d !== void 0 ? _d : [];
        for (const upgradeService of upgradeServices) {
            for (const segmentId of upgradeService.segmentIds) {
                if (!upgradeSelectedOnSegments[segmentId] &&
                    (upgradeService.count > 0 || upgradeService.confirmedCount > 0)) {
                    upgradeSelectedOnSegments[segmentId] = true;
                }
            }
        }
    }
    return (Object.keys(upgradeSelectedOnSegments).length ===
        segments.filter(segment => segment.subStatus === null).length);
});
export const getCanAddUpgradeFlightClass = createSelector(getUpgradeClassServices, upgradeServices => !!(upgradeServices === null || upgradeServices === void 0 ? void 0 : upgradeServices.length));
export const getBusinessLounge = createSelector(getServices, services => services.filter(service => service.subType === GdsServiceVipSubType.BusinessLounge));
export const getBusinessLoungeByTypes = createSelector(getServices, services => {
    const businessLoungeByTypeMap = new Map();
    services.forEach(service => {
        var _a;
        const vipService = service;
        if (vipService.subType === GdsServiceVipSubType.BusinessLounge ||
            vipService.subType === GdsServiceVipSubType.BusinessLoungeInternational) {
            businessLoungeByTypeMap.set(vipService.subType, [
                ...((_a = businessLoungeByTypeMap.get(vipService.subType)) !== null && _a !== void 0 ? _a : []),
                vipService
            ]);
        }
    });
    return [...businessLoungeByTypeMap.values()];
});
export const getBaggageDelivery = createSelector([getOrder, getServices], (order, services) => {
    return services.reduce((services, service) => {
        const newServices = [...services];
        if (service.subType ===
            GdsServiceVipSubType.BaggageDelivery) {
            const serviceSegments = [...getRelatedSegments(service.allowedSegments)];
            // WEBSKY-2899: сегмент должен быть без пересадки
            serviceSegments
                .filter(segmentId => {
                var _a, _b;
                return ((_b = (_a = order.flight.segmentGroups.find(group => group.segments.some(segment => segment.id === segmentId))) === null || _a === void 0 ? void 0 : _a.segments) === null || _b === void 0 ? void 0 : _b.length) === 1;
            })
                .forEach(segmentId => newServices.push(Object.assign(Object.assign({}, service), { allowedSegments: [[segmentId]] })));
        }
        return newServices;
    }, []);
});
export const getCapsuleHotel = createSelector(getServices, services => services.filter(service => service.subType === GdsServiceVipSubType.CapsuleHotel));
export const getSmsNotificationServices = createSelector(getServices, services => services.filter(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Vip &&
    service.subType === GdsServiceVipSubType.SMS));
export const getSelectedUpgradeClassServices = createSelector(getUpgradeClassServices, getTravellers, getSelectedTravellerServices);
export const getSelectedUpgradeClassPrice = createSelector(getSelectedUpgradeClassServices, getSelectedServicesTotalPrice);
export const getSelectedTravellerSmsNotifications = createSelector(getSmsNotificationServices, getTravellers, getSelectedTravellerServices);
export const getSelectedSmsNotificationsPrice = createSelector(getSelectedTravellerSmsNotifications, getSelectedServicesTotalPrice);
export const getPriorityBoarding = createSelector(getServices, services => services.filter(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Vip &&
    service.subType ===
        GdsServiceVipSubType.PriorityBoarding));
export const getSelectedTravellerPriorityBoarding = createSelector(getPriorityBoarding, getTravellers, getSelectedTravellerServices);
export const getRefundableTicketServices = createSelector(getServices, services => services.filter(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Vip &&
    service.subType ===
        GdsServiceVipSubType.RefundableTicket));
export const getSelectedTravellerRefundableTicket = createSelector(getRefundableTicketServices, getTravellers, getSelectedTravellerServices);
export const getTravellerRefundableTicket = createSelector(getRefundableTicketServices, getTravellers, (services, travellers) => getSelectedTravellerServices(services, travellers, 'all'));
export const getSelectedRefundableTicketPrice = createSelector(getSelectedTravellerRefundableTicket, getSelectedServicesTotalPrice);
export const getSelectedTravellerBaggageDelivery = createSelector(getBaggageDelivery, getTravellers, getSelectedTravellerServices);
export const getSelectedPriorityBoardingPrice = createSelector(getSelectedTravellerPriorityBoarding, getSelectedServicesTotalPrice);
export const getAdditionalBaggage = createSelector(getBaggage, items => {
    return items.filter(item => item.type === BaggageType.CheckedBaggage);
});
const getFakeBaggage = createSelector(getBaggageServices, (services) => {
    return services.filter(service => service.isFake).map(convertBaggageObject);
});
export const getIncludedBaggage = createSelector(_getIncludedBaggage, getFakeBaggage, (included, fake) => {
    return included.concat(fake);
});
export const getUpgradeBaggage = createSelector(getBaggage, getTravellers, (baggageItems, travellers) => {
    const removableServicesMap = new Map();
    travellers.forEach(traveller => {
        var _a, _b, _c;
        (_c = (_b = (_a = traveller.services) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) === null || _c === void 0 ? void 0 : _c.forEach(service => {
            const key = `${traveller.id}/${service.serviceId}`;
            removableServicesMap.set(key, service.count > 0);
        });
    });
    const isCanBeAddedOrRemoved = (item) => {
        const canBeRemoved = item.allowedPassengers.some(passengerId => removableServicesMap.get(`${passengerId}/${item.id}`));
        return item.canBeAdded || canBeRemoved;
    };
    return baggageItems.filter(item => {
        return ((item.type === BaggageType.HandLuggageExcess || item.type === BaggageType.CarryOn) &&
            isCanBeAddedOrRemoved(item));
    });
});
export const getSpecialEquipment = createSelector(getBaggage, getSpecialEquipmentBaggageTypes, (items, types) => {
    return items.filter(item => types.includes(item.type));
});
export const getOverweightBaggage = createSelector(getBaggage, items => items.filter(item => item.type === BaggageType.BaggageExcess));
export const getUpgradableBaggage = createSelector([getUpgradeBaggage, getAdditionalBaggage, getSpecialEquipment, getOverweightBaggage], (...baggage) => baggage.reduce((services, items) => [...services, ...items], []));
export const getHasEverySelectedSeats = createSelector(getOrder, getPassengerStateCompatible, (order, passengers) => {
    const segmentsCount = order.flight.segments.length;
    return passengers
        .filter(passenger => passenger.type !== AviaPassengerType.INF)
        .some(({ seats = [] }) => {
        const segmentsBySeats = new Set(seats.map(seat => seat.segmentId));
        return seats.length > 0 && segmentsBySeats.size === segmentsCount;
    });
});
export const getHasAnySelectedSeats = createSelector(getPassengerStateCompatible, passengers => {
    return passengers
        .filter(passenger => passenger.type !== AviaPassengerType.INF)
        .some(({ seats = [] }) => {
        return seats.length > 0;
    });
});
export const getSkipRecommendations = createSelector(getIsRedirectFromMetasearch, getIsOddOrderId, (isFromMeta, orderIdIsOdd) => isFromMeta && orderIdIsOdd);
export const getIsTransferSelected = createSelector(getOrder, order => { var _a, _b, _c, _d; return ((_d = (_c = (_b = (_a = order.additionalServices) === null || _a === void 0 ? void 0 : _a.transfer) === null || _b === void 0 ? void 0 : _b.aeroexpress) === null || _c === void 0 ? void 0 : _c.selectedTrips) === null || _d === void 0 ? void 0 : _d.length) > 0; });
export const getIsInsuranceSelected = createSelector(getOrder, order => { var _a, _b, _c; return ((_c = (_b = (_a = order.additionalServices) === null || _a === void 0 ? void 0 : _a.insurances) === null || _b === void 0 ? void 0 : _b.selectedInsurances) === null || _c === void 0 ? void 0 : _c.length) > 0; });
export const getShowSeatRecommendations = createSelector(getSkipRecommendations, getOrderId, getTravellers, getHasEverySelectedSeats, (skipRecommendations, orderId, travellers, getHasEverySelectedSeats) => !skipRecommendations &&
    // Check if we have something to recommend.
    travellers.some(traveller => traveller.preselectedServices.seats.length));
export const getIsNeedToConfirmConsents = createSelector(useConfig, config => {
    return !['A9', 'RM', 'DV', 'JI', '5G', '2S'].includes(config.global.companyInfo.iataCode);
});
const addServiceIntoGroup = (servicesByTypes, type, segmentId, service, timelineStep, canBeAdded = true, selectedServices = []) => {
    var _a;
    const servicePrice = getServicePrice(service);
    const step = getAggregatorGroupType(type, timelineStep);
    if (servicesByTypes[segmentId]) {
        if (servicesByTypes[segmentId][step]) {
            servicesByTypes[segmentId][step].services.push(service);
            if (servicePrice && servicesByTypes[segmentId][step].minPrice.amount > servicePrice.amount) {
                servicesByTypes[segmentId][step].minPrice.amount = servicePrice.amount;
            }
            if (canBeAdded) {
                servicesByTypes[segmentId][step].canAddService = true;
            }
        }
        else {
            servicesByTypes[segmentId][step] = {
                type: type,
                services: [service],
                selectedServices: selectedServices,
                totalPrice: {
                    amount: 0,
                    currency: null
                },
                minPrice: {
                    amount: (_a = servicePrice === null || servicePrice === void 0 ? void 0 : servicePrice.amount) !== null && _a !== void 0 ? _a : 0,
                    currency: servicePrice.currency
                },
                canAddService: canBeAdded,
                step: timelineStep
            };
        }
    }
};
const getRelatedAndSelectedSegmentIdsForService = (service, selectedServices) => {
    var _a;
    const relatedServicesSegments = getRelatedSegments((_a = service.allowedSegments) !== null && _a !== void 0 ? _a : []);
    Object.keys(selectedServices).forEach(passengerId => {
        if (selectedServices[passengerId].hasOwnProperty(service.id)) {
            selectedServices[passengerId][service.id].forEach(selected => selected.segments.forEach(segmentId => relatedServicesSegments.add(segmentId)));
        }
    });
    return [...relatedServicesSegments];
};
export const getGdsServicesBySegments = createSelector([
    getOrder,
    getSegments,
    getSelectedServicesState,
    getBrandIncludedBaggage,
    getServices,
    getSeatMap,
    getCanBeAddedInsurancesByGroups,
    getAeroexpress,
    getEveryPortServices,
    useConfig
], (order, segments, selectedServices, brandIncluded, gdsServices, seatMap, insurances, aeroexpress, everyPort, config) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const servicesByTypes = {};
    segments.forEach(segment => {
        servicesByTypes[segment.id] = {};
    });
    const serviceIdTypes = new Map();
    const gdsServiceMap = new Map();
    gdsServices.forEach(service => {
        var _a, _b, _c;
        gdsServiceMap.set(service.id, service);
        let type = service.type, convertedService, canBeAdded = true, isRecommended = false;
        if (service.type === OrderAdditionalServiceGdsServiceServiceType.Seat) {
            return;
        }
        else if (service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage) {
            const baggageService = service;
            if (isPetBaggage(baggageService) && ((_b = (_a = config.global) === null || _a === void 0 ? void 0 : _a.servicesAvailable) === null || _b === void 0 ? void 0 : _b.animal)) {
                type = 'animal';
            }
            convertedService = Object.assign(Object.assign({}, convertBaggageObject(baggageService)), { _serviceType: 'gdsService' });
            canBeAdded = service.canBeAdded;
        }
        else if (service.type === OrderAdditionalServiceGdsServiceServiceType.Meal) {
            convertedService = Object.assign(Object.assign({}, convertMealObject(service)), { _serviceType: 'gdsService' });
            canBeAdded = service.canBeAdded;
        }
        else if (service.type === OrderAdditionalServiceGdsServiceServiceType.Vip) {
            const vipService = service;
            if (vipService.subType === GdsServiceVipSubType.BaggageDelivery) {
                type = 'baggageDelivery';
            }
            if (vipService.subType === GdsServiceVipSubType.Upgrade) {
                type = 'upgrade';
            }
            convertedService = Object.assign(Object.assign({}, vipService), { _serviceType: 'gdsService' });
        }
        else if (service.type === OrderAdditionalServiceGdsServiceServiceType.Misc) {
            const miscService = service;
            if (miscService.isRecommended) {
                isRecommended = true;
            }
            convertedService = Object.assign(Object.assign({}, miscService), { _serviceType: 'gdsService' });
        }
        else {
            convertedService = Object.assign(Object.assign({}, service), { _serviceType: 'gdsService' });
        }
        const relatedSegments = getRelatedSegments((_c = service.allowedSegments) !== null && _c !== void 0 ? _c : []);
        serviceIdTypes.set(service.id, type);
        getRelatedAndSelectedSegmentIdsForService(service, selectedServices).forEach(segmentId => {
            var _a;
            const timelineStep = (_a = service.timelineStep) !== null && _a !== void 0 ? _a : getDefaultTimelineStep(type);
            addServiceIntoGroup(servicesByTypes, type, segmentId, convertedService, timelineStep, relatedSegments.has(segmentId) && canBeAdded);
            if (isRecommended && timelineStep !== TimelineStep.Recommended) {
                addServiceIntoGroup(servicesByTypes, type, segmentId, convertedService, TimelineStep.Recommended, relatedSegments.has(segmentId) && canBeAdded);
            }
        });
    });
    if ((_a = order.ffpInfo) === null || _a === void 0 ? void 0 : _a.ffpSupported) {
        segments.forEach(segment => {
            const segmentId = segment.id;
            const loyaltyType = 'loyalty';
            const timelineStep = getDefaultTimelineStep(loyaltyType);
            servicesByTypes[segmentId][loyaltyType] = {
                type: loyaltyType,
                step: timelineStep,
                services: [],
                selectedServices: [],
                canAddService: true
            };
        });
    }
    brandIncluded.forEach(service => {
        service.baggage.includedSegments.forEach(segmentId => {
            var _a;
            if ((_a = servicesByTypes[segmentId]) === null || _a === void 0 ? void 0 : _a[OrderAdditionalServiceGdsServiceServiceType.Baggage]) {
                servicesByTypes[segmentId][OrderAdditionalServiceGdsServiceServiceType.Baggage].selectedServices.push({
                    count: service.count,
                    type: OrderAdditionalServiceGdsServiceServiceType.Baggage,
                    service: Object.assign(Object.assign({}, service.baggage), { _serviceType: 'gdsService' })
                });
            }
        });
    });
    seatMap.segments.forEach(segment => {
        const minPrice = {
            amount: Infinity,
            currency: 'RUB'
        };
        segment.decks.forEach(desk => {
            desk.rows.forEach(row => row.seats.forEach(seat => {
                if (seat.isAvailable && minPrice.amount > seat.price.amount) {
                    minPrice.amount = seat.price.amount;
                    minPrice.currency = seat.price.currency;
                }
            }));
        });
        servicesByTypes[segment.segmentsInfo.id][OrderAdditionalServiceGdsServiceServiceType.Seat] = {
            type: OrderAdditionalServiceGdsServiceServiceType.Seat,
            canAddService: segment.availableSeatsCount > 0,
            services: [],
            selectedServices: [],
            minPrice,
            totalPrice: {
                amount: 0,
                currency: Currency.RUB
            },
            step: getDefaultTimelineStep(OrderAdditionalServiceGdsServiceServiceType.Seat)
        };
    });
    if ((_b = aeroexpress === null || aeroexpress === void 0 ? void 0 : aeroexpress.trips) === null || _b === void 0 ? void 0 : _b.length) {
        aeroexpress.trips.forEach(trip => {
            var _a;
            (_a = trip.segments) === null || _a === void 0 ? void 0 : _a.forEach(segment => {
                var _a;
                addServiceIntoGroup(servicesByTypes, 'transfer', segment.id, Object.assign(Object.assign({}, trip), { _serviceType: 'aeroexpress' }), getDefaultTimelineStep('transfer', (_a = trip.toStation) === null || _a === void 0 ? void 0 : _a.direction));
            });
        });
        aeroexpress.selectedTrips.forEach(selectedTrip => {
            var _a;
            (_a = selectedTrip.segments) === null || _a === void 0 ? void 0 : _a.forEach(segment => {
                var _a, _b;
                (_b = servicesByTypes[segment.id][getAggregatorGroupType('transfer', getDefaultTimelineStep('transfer', (_a = selectedTrip.toStation) === null || _a === void 0 ? void 0 : _a.direction))].selectedServices) === null || _b === void 0 ? void 0 : _b.push({
                    count: 1,
                    type: 'transfer',
                    service: Object.assign(Object.assign({}, selectedTrip), { _serviceType: 'aeroexpress' })
                });
            });
        });
    }
    if (insurances.length) {
        Object.keys(servicesByTypes).forEach(segmentId => {
            servicesByTypes[segmentId]['insuranceGroups'] = {
                type: 'insuranceGroups',
                services: [
                    {
                        _serviceType: 'insuranceGroups',
                        groups: insurances
                    }
                ],
                selectedServices: [],
                canAddService: true
            };
        });
    }
    if (everyPort === null || everyPort === void 0 ? void 0 : everyPort.services) {
        const everyPortSelectedServices = (_d = (_c = everyPort.selectedServices) === null || _c === void 0 ? void 0 : _c.map(service => ({
            service: Object.assign(Object.assign({}, everyPort.services.find(({ id }) => id === service.product.id)), { _serviceType: 'nas' }),
            type: service.product.category,
            count: 1
        }))) !== null && _d !== void 0 ? _d : [];
        everyPort.services.forEach(everyPortService => {
            var _a;
            const segmentId = everyPortService.segmentId.toString();
            addServiceIntoGroup(servicesByTypes, everyPortService.category, segmentId, Object.assign(Object.assign({}, everyPortService), { _serviceType: 'nas' }), (_a = everyPortService.timelineStep) !== null && _a !== void 0 ? _a : getDefaultTimelineStep(everyPortService.category, everyPortService.direction), order.status !== OrderStatus.Cancelled && everyPortService.canBeAdded, everyPortSelectedServices);
        });
    }
    for (const services of Object.values(selectedServices)) {
        for (const [serviceId, selectedServices] of Object.entries(services)) {
            const gdsService = gdsServiceMap.get(serviceId);
            const cachedServiceType = serviceIdTypes.get(serviceId);
            for (const selectedService of selectedServices) {
                const { servicePayload } = selectedService;
                if (!(servicePayload === null || servicePayload === void 0 ? void 0 : servicePayload.type)) {
                    continue;
                }
                const type = cachedServiceType !== null && cachedServiceType !== void 0 ? cachedServiceType : servicePayload.type;
                const timelineStep = (_e = gdsService === null || gdsService === void 0 ? void 0 : gdsService.timelineStep) !== null && _e !== void 0 ? _e : getDefaultTimelineStep(type);
                const serviceType = getAggregatorGroupType(type, timelineStep);
                const isSeatServiceType = type === OrderAdditionalServiceGdsServiceServiceType.Seat;
                for (const segmentId of selectedService.segments) {
                    const segmentServices = (_g = (_f = servicesByTypes[segmentId]) === null || _f === void 0 ? void 0 : _f[serviceType]) !== null && _g !== void 0 ? _g : (_h = servicesByTypes[segmentId]) === null || _h === void 0 ? void 0 : _h[type];
                    if (!segmentServices) {
                        continue;
                    }
                    const gdsAggregationService = isSeatServiceType
                        ? Object.assign(Object.assign({}, servicePayload), { _serviceType: 'seat' })
                        : (_j = segmentServices.services) === null || _j === void 0 ? void 0 : _j.find(s => 'id' in s && s.id === serviceId);
                    segmentServices.selectedServices.push({
                        type,
                        count: selectedService.number,
                        service: gdsAggregationService
                    });
                    const servicePrice = getServicePrice(gdsAggregationService);
                    if (servicePrice) {
                        segmentServices.totalPrice.amount += servicePrice.amount;
                        segmentServices.totalPrice.currency = servicePrice.currency;
                    }
                }
            }
        }
    }
    const aggregatedServices = [];
    Object.keys(servicesByTypes).forEach(segmentId => {
        const services = [];
        Object.keys(servicesByTypes[segmentId]).forEach(type => {
            services.push(servicesByTypes[segmentId][type]);
        });
        aggregatedServices.push({
            segmentId,
            services
        });
    });
    return aggregatedServices;
});
export const getTravellersValueNames = createSelector(getTravellers, travellers => {
    const map = new Map();
    if ((travellers === null || travellers === void 0 ? void 0 : travellers.length) !== 0) {
        travellers[0].values.forEach(v => {
            map.set(v.type, v.name);
        });
    }
    return map;
});
export const getHasRequestedOrderServices = createSelector(getOrder, getIsBookedOrConfirmed, getTravellers, (order, isBookedOrConfirmed, travellers) => {
    return isBookedOrConfirmed && getHasRequestedServices(travellers, order.ssrData);
});
export const getExistGdsServicesOrInsurances = createSelector(getServices, getInsurances, (gdsServices, insurances) => {
    var _a;
    return gdsServices.length > 0 || ((_a = insurances === null || insurances === void 0 ? void 0 : insurances.insurancePrograms) === null || _a === void 0 ? void 0 : _a.length) > 0;
});
export const getSplittedOrdersIds = createSelector(getOrder, order => order.splittedOrdersIds);
export const getIsSplittedOrder = createSelector(getSplittedOrdersIds, splittedOrdersIds => !!(splittedOrdersIds === null || splittedOrdersIds === void 0 ? void 0 : splittedOrdersIds.length));
